import { useState, useMemo } from 'react'
import { EmptyState } from '@/shared/components/EmptyState'
import { formatCount } from '@/lib/format'
import { StatCell } from '@/shared/components/StatCell'

/**
 * One UTC day's count. Structural rather than imported so this serves both the per-workspace
 * activity series and the org-wide analytics series, which are the same shape from two endpoints.
 */
export interface PushCadencePoint {
  date: string
  count: number
}

export interface PushCadenceChartProps {
  /** Sparse - days with no pushes are omitted by the server and zero-filled here. */
  pushes: PushCadencePoint[]
  /** Server-owned window length, so a caption never hardcodes 30 or 90. */
  windowDays: number
  /** Noun for the caption/aria text: the dashboard counts pushes across a whole org. */
  subject?: string
  /**
   * Plural of `subject`. Defaults to `${subject}es`, which is right for "push" and wrong for
   * almost anything else - "refusal" would render "refusales". Every noun in the empty state, the
   * stat label, the aria-label and the caption comes from here.
   */
  plural?: string
  /**
   * Replaces the empty state's subtitle. The default names state-version push time, which is only
   * true for the cadence call sites; any other series needs to say where its own days come from.
   */
  emptySubtitle?: string
  /**
   * The built-in 4-cell summary strip. Turn it OFF when the caller already shows those numbers -
   * two stat strips repeating one figure forty pixels apart reads as a bug.
   */
  showStats?: boolean
}


/**
 * Pushes per UTC day over the server's activity window.
 *
 * Replaces a per-serial duration bar chart whose own caption had to admit "bar width carries no
 * time meaning" - here the x axis IS time, so position carries real elapsed time.
 *
 * A line over a ZERO-FILLED domain, which is the part that makes it honest: a day with no push
 * plots at 0 rather than being skipped, so the line drops to the axis instead of being drawn
 * straight across the gap. It never interpolates between two distant active days.
 */
export function PushCadenceChart({
  pushes,
  windowDays,
  subject = 'push',
  plural,
  emptySubtitle,
  showStats = true,
}: PushCadenceChartProps) {
  // Defaults chosen so both existing cadence call sites render byte-identically to before.
  const many = plural ?? `${subject}es`
  const Many = `${many[0]!.toUpperCase()}${many.slice(1)}`
  const [hovered, setHovered] = useState<number | null>(null)

  // Shared UTC date domain, oldest -> today. The worker buckets by strftime/'unixepoch' (UTC
  // days), so the client domain must be UTC too - never locale days, which would silently drop or
  // duplicate a bucket for every viewer west of UTC. Epoch-day arithmetic floors to a UTC
  // boundary before expanding (the DownloadsSeriesChart form).
  const days = useMemo(() => {
    const today = Math.floor(Date.now() / 86400000)
    return Array.from({ length: windowDays }, (_, i) =>
      new Date((today - (windowDays - 1) + i) * 86400000).toISOString().slice(0, 10),
    )
  }, [windowDays])

  // The endpoint returns only days that had pushes, so zero-fill onto the domain above.
  const counts = useMemo(() => {
    const byDate = new Map(pushes.map((p) => [p.date, p.count]))
    return days.map((d) => byDate.get(d) ?? 0)
  }, [pushes, days])

  const total = counts.reduce((a, b) => a + b, 0)
  const activeDays = counts.filter((c) => c > 0).length
  const max = Math.max(1, ...counts)

  if (total === 0) {
    return (
      <EmptyState
        icon="◷"
        title={`No ${many} in the last ${windowDays} days`}
        subtitle={emptySubtitle
          ?? `${Many} are counted per UTC day from each state version's push time.`}
      />
    )
  }

  const VB_W = 300
  const VB_H = 100
  const PAD_Y = 8
  const n = days.length
  const xAt = (i: number) => (i / (n - 1)) * VB_W
  const yAt = (c: number) => VB_H - PAD_Y - (c / max) * (VB_H - PAD_Y * 2)
  const linePath = counts.map((c, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(c).toFixed(1)}`).join(' ')
  const tickIdx = [0, Math.floor((n - 1) * 0.25), Math.floor((n - 1) * 0.5), Math.floor((n - 1) * 0.75), n - 1]

  return (
    <div>
      {/* 38, not the usual 14: the hover tooltip is anchored above the plot (translateY(-100%)),
          so without reserved room it lands on top of the stat cells. */}
      {showStats && (
        <div className="stat-strip" style={{ marginBottom: 38 }}>
          <StatCell label={`${many} · ${windowDays}d`} value={formatCount(total)} />
          <StatCell label="active days" value={`${formatCount(activeDays)} / ${windowDays}`} />
          <StatCell label="busiest day" value={formatCount(max)} />
          <StatCell label="mean · active day" value={activeDays > 0 ? (total / activeDays).toFixed(1) : '—'} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <div aria-hidden style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 120, fontFamily: 'var(--mono)', fontSize: '0.55rem', color: 'var(--text3)', textAlign: 'right', flexShrink: 0 }}>
          <span>{formatCount(max)}</span>
          <span>{formatCount(Math.round(max / 2))}</span>
          <span>0</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {hovered !== null && (
            <div
              style={{
                position: 'absolute',
                // Clamp to [4%, 96%] so a hover at either end cannot push the tooltip outside the
                // modal panel's overflow:hidden.
                left: `${Math.min(96, Math.max(4, ((hovered + 0.5) / n) * 100))}%`,
                top: -2,
                transform: `translate(${hovered < n / 6 ? '0' : hovered > (5 * n) / 6 ? '-100%' : '-50%'}, -100%)`,
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '5px 8px',
                fontFamily: 'var(--mono)',
                fontSize: '0.62rem',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <div style={{ color: 'var(--text2)' }}>{days[hovered]}</div>
              <div style={{ fontWeight: 700 }}>
                {formatCount(counts[hovered]!)} {counts[hovered] === 1 ? subject : many}
              </div>
            </div>
          )}
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="none"
            width="100%"
            height={120}
            role="img"
            aria-label={`${Many} per day over the last ${windowDays} days: ${total} ${many} across ${activeDays} active days, busiest day ${max}.`}
            style={{ overflow: 'visible' }}
            onMouseLeave={() => setHovered(null)}
          >
            <path
              d={linePath}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            {hovered !== null && (
              <>
                <line
                  x1={xAt(hovered)} y1={0} x2={xAt(hovered)} y2={VB_H}
                  stroke="var(--text3)" strokeWidth={1} strokeDasharray="2 2"
                  vectorEffect="non-scaling-stroke"
                />
                {/* Drawn as two ellipses, not a circle: the viewBox is stretched by
                    preserveAspectRatio="none", so a circle would render as a flattened oval. */}
                <ellipse
                  cx={xAt(hovered)} cy={yAt(counts[hovered]!)}
                  rx={VB_W / 220} ry={VB_H / 55}
                  fill="var(--accent)" stroke="var(--bg2)" strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
            {/* Full-height transparent hit targets - a 2px line is unhoverable otherwise. */}
            {counts.map((_, i) => (
              <rect
                key={`hit-${days[i]}`}
                x={i * (VB_W / n)}
                y={0}
                width={VB_W / n}
                height={VB_H}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
              />
            ))}
          </svg>
          <div aria-hidden style={{ position: 'relative', height: 12, marginTop: 6 }}>
            {tickIdx.map((idx) => (
              <span
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(idx / (n - 1)) * 100}%`,
                  transform: `translateX(${idx === 0 ? '0' : idx === n - 1 ? '-100%' : '-50%'})`,
                  fontFamily: 'var(--mono)',
                  fontSize: '0.55rem',
                  color: 'var(--text3)',
                  whiteSpace: 'nowrap',
                }}
              >
                {days[idx]!.slice(5)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', marginTop: 8 }}>
        {/* `subject` singular here: "a day with no refusal", not "no refusals". */}
        // One point per UTC day over the last {windowDays} days. A day with no {subject} plots at
        zero, so a dip to the axis is a real gap - the line is never drawn across one.
      </div>
    </div>
  )
}
